import {
  type EmbedClientContext,
  getUdsResultTooLargeDetails,
  getUdsSqlErrorMetadata,
  renderUdsRowLimitError,
  UDS_RESULT_TOO_LARGE_ERROR_CODE,
  UDS_ROW_LIMIT_TOO_LARGE_ERROR_CODE,
} from '@doss/common-all'
import { type GuestDestination, type TableMappings } from '@doss/configbase-common/apps'
import { compileGuestSource, lintGuestSource } from '@doss/configbase-tooling/apps'
import {
  GUEST_FRAME_PATH,
  GUEST_FRAME_PROTOCOL_VERSION,
  GUEST_ENDPOINT_BUDGETS,
  GUEST_NAVIGATION_BUDGETS,
  GUEST_QUERY_BUDGETS,
  isFrameAnnotationCopyMessage,
  isFrameDestinationRequestMessage,
  isFrameDestinationResolveMessage,
  isFrameEndpointCallMessage,
  isFrameGraphqlRequestMessage,
  isFrameDiagnosticsMessage,
  isFrameHistoryGoMessage,
  isFrameNavigateMessage,
  isFrameNavigationRequestMessage,
  isFrameQueriesSettledMessage,
  isFrameReadyMessage,
  isFrameRenderedMessage,
  isFrameRuntimeErrorMessage,
  isFrameResourceSnapshotMessage,
  isFrameSqlRequestMessage,
  isFrameTitleMessage,
  isRequestId,
  parseGuestHref,
  sanitizeGuestPath,
  sanitizeGuestSearch,
  type FrameDiagnosticEntry,
  type FrameDestinationResolveMessage,
  type FrameEndpointCallMessage,
  type FrameEndpointErrorMessage,
  type FrameEndpointResultMessage,
  type FrameGraphqlErrorMessage,
  type FrameGraphqlRequestMessage,
  type FrameGraphqlResultMessage,
  type FrameQueryErrorMessage,
  type FrameRuntimeErrorPhase,
  type FrameSqlRequestMessage,
  type FrameResourceSnapshotMessage,
} from '@doss/guest-frame'
import {
  APP_SDK_PROTOCOL_VERSION,
  isAppFrameReadyMessage,
  isAppSdkReadyMessage,
  validateAppSdkGraph,
  composeAppFrame,
} from '@doss/guest-frame/app-sdk'
import appBootstrap from '@doss/guest-frame/generated/app-bootstrap.json'
import { Bug } from 'lucide-react'
import { memo, useEffect, useId, useMemo, useRef, useState } from 'react'
import constants from '~/common/config/config'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { buildTraceparent, newSpanId, randomHex } from '~/common/telemetry/client-spans'
import {
  recordGuestQueryObservation,
  recordGuestSpanObservation,
  registerGuestFrame,
  type GuestFrameErrorCode,
  type GuestQueryErrorCode,
} from '~/common/telemetry/guest-frame-monitor'
import {
  captureGuestFrameFailure,
  type GuestFrameFailurePhase,
} from '~/common/telemetry/guest-frame-sentry'
import { GuestFrameDebugOverlay } from './GuestFrameDebugOverlay'
import { launchProtocolHandler, useOpenTabWithFallback } from './host-navigation'
import type { GuestSqlTransportPromise } from './transport'
import {
  EndpointCallError,
  useGuestEndpointTransport,
  useGuestGraphqlTransport,
  useGuestSqlBatchTransport,
  useGuestSqlTransport,
} from './transport'

/** A same-origin link requested by the guest, in host URL space. */
export type HostNavigationRequest = {
  pathname: string
  search: string
  newTab: boolean
}

/** A guest link out of its app by logical id, for the mount to resolve to a host route. */
export type GuestDestinationRequest = {
  destination: GuestDestination
  newTab: boolean
}

/** Host URL state in guest route space; `key` is react-router's location.key. */
export type GuestFrameRoute = {
  path: string
  search: string
  key: string
}

/** A sanitized, budget-tagged guest navigation intent, in guest route space. */
export type GuestNavigationIntent = {
  path: string
  search: string
  replace: boolean
}

/** A pinned App version: the SHA-256 of `source` (the stored compiled module) and the exact SDK graph it runs on. */
export type EsmPin = {
  sha256: string
  graph: unknown
  /** The version's deploy-resolved table mappings, handed over at init. */
  mappings?: TableMappings
}

export type GuestFrameProps = {
  title: string
  source: string
  /** Run `source` through the pinned ESM path; absent = legacy (CJS) path compiling `source`. */
  esm?: EsmPin | null
  /** Dashboard attribution stamped on this frame's client spans (embed surfaces). */
  telemetryContext?: EmbedClientContext
  // Component-level default is on, but the embed host shows the tiling only when the backing
  // embed opts in (Embed.betaWatermarkDisabled === false, flipped directly in the database) —
  // org embeds hide it by default.
  showBetaWatermark?: boolean
  // Same-origin guest navigation requests, already classified: navigate the app in place or open a
  // new tab. External hrefs never reach this callback — the frame host opens them directly.
  onHostNavigationRequest?: (link: HostNavigationRequest) => void
  // Links out of the app by logical id (a platform object, another app's route), already
  // validated: the mount resolves the id to its existing host route and navigates it.
  onGuestDestinationRequest?: (request: GuestDestinationRequest) => void
  // Resolves a destination ahead of any click (hover, focus, context menu), so the guest's anchor
  // can carry the real host URL for copy link and the status bar. Absent answers null.
  resolveGuestDestination?: (destination: GuestDestination) => Promise<string | null>
  // Present = URL-synced mount; absent = memory mode. The MOUNT owns sanitizing this value
  // (the frame commits echoes as-is); GuestFrame sanitizes only frame-originated traffic.
  route?: GuestFrameRoute
  // Receives sanitized guest navigation intents; the mount owns the URL translation.
  onGuestNavigate?: (intent: GuestNavigationIntent) => void
  // Guest history traversals (navigate(delta)/go), for the mount to run as history.go.
  onGuestHistoryGo?: (delta: number) => void
  // Host URL prefix of the guest route space (e.g. `/embed/${embedId}`), for new-tab opens.
  guestSpaceHrefPrefix?: string
  // The guest document's title; the mount appends it to the web title ('' clears).
  onGuestTitle?: (title: string) => void
}

type PendingResourceSnapshot = {
  resolve: (snapshot: FrameResourceSnapshotMessage) => void
  reject: (error: Error) => void
  timeoutId: number
}
const FRAME_BOOT_TIMEOUT_MS = 5_000
const RESOURCE_SNAPSHOT_TIMEOUT_MS = 30_000
const FIRST_RESOURCE_REQUEST_ID = 1_000_000

const ERROR_CODE_BY_RUNTIME_PHASE: Record<FrameRuntimeErrorPhase, GuestFrameErrorCode> = {
  module_load: 'module_load_error',
  render: 'render_error',
  unhandled_error: 'unhandled_error',
  unhandled_rejection: 'unhandled_rejection',
}

const queryErrorCode = (error: unknown): GuestQueryErrorCode => {
  const code = getUdsSqlErrorMetadata(error).code
  if (code === UDS_RESULT_TOO_LARGE_ERROR_CODE || code === UDS_ROW_LIMIT_TOO_LARGE_ERROR_CODE) {
    return code
  }
  const message = error instanceof Error ? error.message : String(error)
  return /timed?\s*out|timeout/i.test(message) ? 'query_timeout' : 'query_failed'
}

type QueryErrorMetadata = Omit<FrameQueryErrorMessage, 'type' | 'id' | 'error'>

// Group page fetches while excluding moving continuations from telemetry identity.
const statementKeyOf = (message: FrameSqlRequestMessage): string =>
  JSON.stringify({
    sql: message.sql,
    options: {
      page: {
        limit: message.page.limit,
        mode: message.page.offset === undefined ? 'keyset' : 'offset',
      },
      includeTotalCount: message.includeTotalCount === true,
    },
  })

/** A call's failure as the frame reads it: the service's code and status, when it got that far. */
const endpointErrorOf = (id: number, error: unknown): FrameEndpointErrorMessage => ({
  type: 'guest-endpoint-error',
  id,
  error: error instanceof Error ? error.message : String(error),
  code: error instanceof EndpointCallError ? error.code : undefined,
  status: error instanceof EndpointCallError ? error.status : undefined,
})

/** UTF-8 bytes of an input object's JSON; null when it has none (a cycle, a BigInt). */
const jsonByteLength = (value: Record<string, unknown>): number | null => {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length
  } catch {
    return null
  }
}

const recordClientQueryFailure = (errorCode: GuestQueryErrorCode): void => {
  recordGuestQueryObservation({
    status: 'failed',
    source: 'client',
    errorCode,
    durationMs: 0,
  })
}

/** What boots in the sandbox, or why nothing can. */
type FrameBoot =
  | { kind: 'legacy'; code: string }
  | { kind: 'esm'; sha256: string; srcdoc: string; mappings?: TableMappings }
  | { kind: 'error'; path: 'legacy' | 'esm'; code: GuestFrameErrorCode; message: string }

const resolveBoot = (source: string, esm: EsmPin | null | undefined): FrameBoot => {
  // LEGACY_CJS_REMOVE: ENG-601 — Retire this compile fallback and GUEST_FRAME_PATH loading at cutover.
  if (esm === null || esm === undefined) {
    const compiled = compileGuestSource(source)
    return compiled.error === undefined
      ? { kind: 'legacy', code: compiled.code }
      : { kind: 'error', path: 'legacy', code: 'compile_error', message: compiled.error.message }
  }
  // The graph is validated against this host's shell: an older build fails here, before boot.
  try {
    const graph = validateAppSdkGraph(esm.graph)
    const srcdoc = composeAppFrame(
      { expectedParentOrigin: window.location.origin, assetBase: constants.appSdkAssetBase, graph },
      appBootstrap.code,
    )
    return { kind: 'esm', sha256: esm.sha256, srcdoc, mappings: esm.mappings }
  } catch (error) {
    return {
      kind: 'error',
      path: 'esm',
      code: 'protocol_mismatch',
      message: error instanceof Error ? error.message : String(error),
    }
  }
}

/**
 * One guest = one sandboxed iframe. The sandbox attribute grants scripts and downloads.
 *
 * Blocked:
 * 1) Same-origin(opaque origin: cookies, storage, or GraphQL),
 * 2) Top-navigation, Popups.
 * 3) Network egress (frame document's CSP)
 * Guest code talks to the host exclusively through the postMessage RPC handled below;.
 */
export const GuestFrame = memo(function GuestFrame({
  title,
  source,
  esm,
  telemetryContext,
  showBetaWatermark = true,
  onHostNavigationRequest,
  onGuestDestinationRequest,
  resolveGuestDestination,
  route,
  onGuestNavigate,
  onGuestHistoryGo,
  guestSpaceHrefPrefix,
  onGuestTitle,
}: GuestFrameProps): JSX.Element {
  const { t } = useTypedTranslation()
  const boot = useMemo(() => resolveBoot(source, esm), [esm, source])
  // Latest-refs: link and route handling must never join the boot effect's dependencies — an
  // identity change there tears down and re-boots the whole frame, destroying guest state.
  const onHostNavigationRequestRef = useRef(onHostNavigationRequest)
  onHostNavigationRequestRef.current = onHostNavigationRequest
  const onGuestDestinationRequestRef = useRef(onGuestDestinationRequest)
  onGuestDestinationRequestRef.current = onGuestDestinationRequest
  const resolveGuestDestinationRef = useRef(resolveGuestDestination)
  resolveGuestDestinationRef.current = resolveGuestDestination
  const routeRef = useRef(route)
  routeRef.current = route
  const onGuestNavigateRef = useRef(onGuestNavigate)
  onGuestNavigateRef.current = onGuestNavigate
  const onGuestHistoryGoRef = useRef(onGuestHistoryGo)
  onGuestHistoryGoRef.current = onGuestHistoryGo
  const onGuestTitleRef = useRef(onGuestTitle)
  onGuestTitleRef.current = onGuestTitle
  const guestSpaceHrefPrefixRef = useRef(guestSpaceHrefPrefix)
  guestSpaceHrefPrefixRef.current = guestSpaceHrefPrefix
  const openExternalTab = useOpenTabWithFallback()
  const openExternalTabRef = useRef(openExternalTab)
  openExternalTabRef.current = openExternalTab
  // The frame only ever sees compiled code, so findings are computed here. An App build was
  // checked by the CLI before publish, so the ESM path has none.
  const lintFindings = useMemo(
    () => (boot.kind === 'legacy' ? lintGuestSource(source) : []),
    [boot.kind, source],
  )
  const runSql = useGuestSqlTransport()
  const runSqlBatch = useGuestSqlBatchTransport()
  // Read through a ref: a new transport must not re-boot the frame.
  const runEndpoint = useGuestEndpointTransport()
  const runEndpointRef = useRef(runEndpoint)
  runEndpointRef.current = runEndpoint
  const runGraphql = useGuestGraphqlTransport()
  const runGraphqlRef = useRef(runGraphql)
  runGraphqlRef.current = runGraphql
  // Internal (doss) users get the in-jail Agentation feedback toolbar; the frame itself has no
  // idea who is looking at it, so the host decides at init time.
  const { isInternal } = useGlobalContext()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const rpcPortRef = useRef<MessagePort | null>(null)
  const [frameError, setFrameError] = useState<string | undefined>(undefined)
  // Errors the guest survived: an uncaught throw or rejection outside render keeps the app up.
  const [runtimeErrors, setRuntimeErrors] = useState<readonly string[]>([])
  // Replaced wholesale on each relay, so rows clear when the frame clears them.
  const [diagnostics, setDiagnostics] = useState<readonly FrameDiagnosticEntry[]>([])
  // Escape containment. Programmatic navigation of the frame document cannot be blocked from
  // inside the sandbox (location is unforgeable; the Navigation API's navigate event is not
  // cancelable in subframes), but the iframe's load event fires on EVERY navigation of the
  // frame — cross-origin included — in every browser. The first load per mount is the frame
  // document booting; any later load means the guest navigated the frame away, so the host
  // remounts the sandbox: a fresh browsing context restores the embed and discards the
  // escaped document together with its joint session-history entries (no trapped Back).
  const [frameEpoch, setFrameEpoch] = useState(0)
  const frameLoadCountRef = useRef(0)
  const handleFrameLoad = (): void => {
    frameLoadCountRef.current += 1
    if (frameLoadCountRef.current > 1) {
      console.error('Guest frame navigated away from the sandbox document; remounting')
      setFrameEpoch((epoch) => epoch + 1)
    }
  }

  useEffect(() => {
    // A new epoch is a new iframe element (key includes it): restart the load count and run
    // a fresh handshake against the remounted browsing context.
    frameLoadCountRef.current = 0
    const monitor = registerGuestFrame(
      iframeRef.current,
      telemetryContext,
      boot.kind === 'error' ? boot.path : boot.kind,
    )
    if (boot.kind === 'error') {
      monitor.failed(boot.code)
      return monitor.unregister
    }

    setFrameError(undefined)
    setRuntimeErrors([])
    setDiagnostics([])
    let initialized = false
    let activeRequests = 0
    // MessagePort ordering guarantees that initial useSql requests arrive before the
    // first-commit marker; that marker closes the render-scoped backend batch.
    let initialRenderCommitted = false
    const initialSqlRequests: FrameSqlRequestMessage[] = []
    // Timestamps of guest history pushes inside the current budget window (see guest-navigate).
    const pushTimestamps: number[] = []
    let nextResourceRequestId = FIRST_RESOURCE_REQUEST_ID
    let runtimeBuildSha: string | undefined
    let failureCaptured = false
    const captureFailureOnce = (
      phase: GuestFrameFailurePhase,
      buildSha = runtimeBuildSha,
    ): void => {
      if (failureCaptured) {
        return
      }
      failureCaptured = true
      captureGuestFrameFailure({
        phase,
        ...(buildSha === undefined ? {} : { runtimeBuildSha: buildSha }),
      })
    }
    const pendingResourceSnapshots = new Map<number, PendingResourceSnapshot>()
    // Group page requests for telemetry without emitting SQL.
    const statementObservations = new Map<
      string,
      { statementId: string; nextRequestIndex: number }
    >()
    const bootTimer = window.setTimeout(() => {
      setFrameError('Guest sandbox did not initialize within 5 seconds')
      monitor.failed('boot_timeout')
      captureFailureOnce('boot_timeout')
    }, FRAME_BOOT_TIMEOUT_MS)

    const rejectRequest = (
      port: MessagePort,
      id: number,
      error: string,
      metadata: QueryErrorMetadata = {},
    ): void => {
      port.postMessage({ type: 'guest-query-error', id, error, ...metadata })
    }

    const requestResourceSnapshot = (
      port: MessagePort,
      includeMemory: boolean,
    ): Promise<FrameResourceSnapshotMessage> => {
      const id = nextResourceRequestId
      nextResourceRequestId += 1
      const { promise, resolve, reject } = Promise.withResolvers<FrameResourceSnapshotMessage>()
      const timeoutId = window.setTimeout(() => {
        pendingResourceSnapshots.delete(id)
        reject(new Error('Guest resource snapshot timed out'))
      }, RESOURCE_SNAPSHOT_TIMEOUT_MS)
      pendingResourceSnapshots.set(id, { resolve, reject, timeoutId })
      port.postMessage({ type: 'guest-resource-snapshot-request', id, includeMemory })
      return promise
    }

    // Requests above the concurrency cap wait here in FIFO order for a free slot.
    const queuedDispatches: Array<() => void> = []
    let disposed = false

    // A settled request frees its slot for the oldest queued one, if the frame is still live.
    const releaseSlot = (): void => {
      activeRequests -= 1
      if (!disposed) {
        queuedDispatches.shift()?.()
      }
    }

    // SQL requests share one per-frame concurrency budget.
    const startRequest = (
      port: MessagePort,
      id: number,
      statementKey: string,
      run: (traceparent: string) => GuestSqlTransportPromise,
      {
        recordFetch = true,
        countConcurrency = true,
        getFetchCompletion,
      }: {
        recordFetch?: boolean
        countConcurrency?: boolean
        getFetchCompletion?: () => Promise<unknown> | undefined
      } = {},
    ): void => {
      if (disposed) {
        return
      }
      const statement = statementObservations.get(statementKey) ?? {
        statementId: randomHex(16),
        nextRequestIndex: 0,
      }
      statementObservations.set(statementKey, {
        ...statement,
        nextRequestIndex: statement.nextRequestIndex + 1,
      })
      const statementObservation = {
        statementId: statement.statementId,
        statementRequestIndex: statement.nextRequestIndex,
      }

      if (countConcurrency) {
        activeRequests += 1
      }
      // During the initial load every fetch parents under the frame-load trace;
      // once it settles, each runtime query becomes its own client-rooted trace.
      const frameTrace = monitor.traceContext()
      const fetchSpanId = newSpanId()
      const fetchTraceId = frameTrace?.traceId ?? randomHex(16)
      const startedAt = performance.now()
      const startedAtEpochMs = Date.now()
      // The fetch span is emitted only for backend-sourced results: a cache hit
      // never left the browser, so there is no fetch to describe.
      const recordFetchSpan = (status: 'ok' | 'error', errorCode?: string): void => {
        if (!recordFetch || (fetchCompletion === undefined && request.source !== 'backend')) {
          return
        }
        recordGuestSpanObservation({
          name: 'graphql.client_fetch',
          traceId: fetchTraceId,
          spanId: fetchSpanId,
          ...(frameTrace === undefined ? {} : { parentSpanId: frameTrace.rootSpanId }),
          startMs: startedAtEpochMs,
          endMs: Date.now(),
          status,
          ...(errorCode === undefined ? {} : { errorCode }),
          ...telemetryContext,
        })
      }
      const request = run(buildTraceparent(fetchTraceId, fetchSpanId))
      const fetchCompletion = recordFetch ? getFetchCompletion?.() : undefined
      void fetchCompletion?.then(
        () => recordFetchSpan('ok'),
        () => recordFetchSpan('error'),
      )
      const dispatch = request.then((result) => {
        if (fetchCompletion === undefined) {
          recordFetchSpan('ok')
        }
        if (rpcPortRef.current !== port) {
          return
        }
        port.postMessage({
          type: 'guest-query-result',
          id,
          rows: result.rows,
          hasNext: result.hasNext,
          ...(result.offset === undefined ? {} : { offset: result.offset }),
          ...(result.cursor === undefined ? {} : { cursor: result.cursor }),
          ...(result.totalCount === undefined ? {} : { totalCount: result.totalCount }),
        })
        recordGuestQueryObservation({
          status: 'succeeded',
          source: request.source,
          durationMs: performance.now() - startedAt,
          ...statementObservation,
          // Cache hits reuse another request's result; its trace never measured THIS duration,
          // so only backend-sourced observations carry the join key.
          ...(request.source === 'backend' && result.traceId !== undefined
            ? { traceId: result.traceId }
            : {}),
        })
      })
      const handledDispatch = dispatch.catch((error: unknown) => {
        const errorCode = queryErrorCode(error)
        if (fetchCompletion === undefined) {
          recordFetchSpan('error', errorCode)
        }
        if (rpcPortRef.current === port) {
          const details = getUdsResultTooLargeDetails(error)
          const fallbackMessage = error instanceof Error ? error.message : String(error)
          const message =
            renderUdsRowLimitError(error) ??
            (details === undefined
              ? fallbackMessage
              : t('embed.resultTooLargeBackend', {
                  limitMiB: details.limitBytes / (1024 * 1024),
                }))
          rejectRequest(port, id, message, getUdsSqlErrorMetadata(error))
        }
        recordGuestQueryObservation({
          status: 'failed',
          source: request.source,
          errorCode,
          durationMs: performance.now() - startedAt,
          ...statementObservation,
        })
      })
      const releaseConcurrency = (): void => {
        if (countConcurrency) {
          releaseSlot()
        }
      }
      void (fetchCompletion ?? handledDispatch).then(releaseConcurrency, releaseConcurrency)
    }

    const dispatchRequest = (
      port: MessagePort,
      id: number,
      statementKey: string,
      run: (traceparent: string) => GuestSqlTransportPromise,
      // Batch statements share one fetch and one HTTP request: only the first statement records
      // the fetch span and occupies a concurrency slot for the whole batch.
      options: {
        recordFetch?: boolean
        countConcurrency?: boolean
        getFetchCompletion?: () => Promise<unknown> | undefined
      } = {},
    ): void => {
      const countConcurrency = options.countConcurrency ?? true
      if (countConcurrency && activeRequests >= GUEST_QUERY_BUDGETS.maxConcurrentQueryRequests) {
        // Queue instead of rejecting: the request dispatches when an in-flight request settles.
        queuedDispatches.push(() => {
          startRequest(port, id, statementKey, run, options)
        })
        return
      }
      startRequest(port, id, statementKey, run, options)
    }

    // Endpoint calls share the budget, uncached here; the frame's QueryClient caches their reads.
    const startEndpointCall = (port: MessagePort, call: FrameEndpointCallMessage): void => {
      activeRequests += 1
      const answer = (message: FrameEndpointResultMessage | FrameEndpointErrorMessage): void => {
        if (rpcPortRef.current === port) {
          port.postMessage(message)
        }
      }
      void runEndpointRef
        .current({ logicalId: call.logicalId, input: call.input })
        .then(
          (output) => answer({ type: 'guest-endpoint-result', id: call.id, output }),
          (error: unknown) => answer(endpointErrorOf(call.id, error)),
        )
        .finally(releaseSlot)
    }

    // App row reads share it too: the frame names one of the host's documents.
    const startGraphql = (port: MessagePort, request: FrameGraphqlRequestMessage): void => {
      activeRequests += 1
      const answer = (message: FrameGraphqlResultMessage | FrameGraphqlErrorMessage): void => {
        if (rpcPortRef.current === port) {
          port.postMessage(message)
        }
      }
      void runGraphqlRef
        .current(request.operation, request.variables)
        .then(
          (data) => answer({ type: 'guest-graphql-result', id: request.id, data }),
          (error: unknown) =>
            answer({
              type: 'guest-graphql-error',
              id: request.id,
              error: error instanceof Error ? error.message : String(error),
            }),
        )
        .finally(releaseSlot)
    }

    // Destination resolves share it too: a guest asks for one per distinct link it renders.
    const startDestinationResolve = (
      port: MessagePort,
      request: FrameDestinationResolveMessage,
    ): void => {
      activeRequests += 1
      const pending =
        resolveGuestDestinationRef.current?.(request.destination) ?? Promise.resolve(null)
      void pending
        .catch(() => null)
        .then((href) => {
          port.postMessage({ type: 'guest-destination-resolved', id: request.id, href })
        })
        .catch((error: unknown) => {
          console.error('Guest destination resolve reply failed', error)
        })
        .finally(releaseSlot)
    }

    const dispatchInitialRender = (port: MessagePort): void => {
      // Concurrency never rejects the initial batch: it occupies one slot and queues if needed.
      const admitted = initialSqlRequests.splice(0)
      if (admitted.length === 0) {
        return
      }

      let batchRequests: readonly GuestSqlTransportPromise[] | undefined
      admitted.forEach((message, index) => {
        dispatchRequest(
          port,
          message.id,
          statementKeyOf(message),
          (traceparent) => {
            batchRequests ??= runSqlBatch(
              admitted.map((statement) => ({
                sql: statement.sql,
                options: {
                  page: statement.page,
                  ...(statement.includeTotalCount === true ? { includeTotalCount: true } : {}),
                  refresh: true,
                  traceparent,
                },
              })),
            )
            const request = batchRequests.at(index)
            if (request !== undefined) {
              return request
            }
            return Object.assign(
              Promise.reject(new Error('UDS SQL batch omitted a statement result')),
              { source: 'backend' as const },
            )
          },
          {
            recordFetch: index === 0,
            countConcurrency: index === 0,
            ...(index === 0
              ? {
                  getFetchCompletion: () => {
                    const backendRequests = batchRequests?.filter(
                      (request) => request.source === 'backend',
                    )
                    return backendRequests === undefined || backendRequests.length === 0
                      ? undefined
                      : Promise.all(backendRequests)
                  },
                }
              : {}),
          },
        )
      })
    }

    const handleRpcMessage = (port: MessagePort, event: MessageEvent<unknown>): void => {
      const data: unknown = event.data
      // ESM path: the shell has loaded the SDK graph and asks for the App build.
      if (boot.kind === 'esm' && isAppSdkReadyMessage(data)) {
        port.postMessage({ type: 'app-bundle', code: source, sha256: boot.sha256 })
        return
      }
      if (isFrameRuntimeErrorMessage(data)) {
        monitor.failed(ERROR_CODE_BY_RUNTIME_PHASE[data.phase])
        captureFailureOnce(data.phase)
        if (data.phase === 'unhandled_error' || data.phase === 'unhandled_rejection') {
          setRuntimeErrors((errors) => [...errors, data.error])
        } else {
          setFrameError(data.error)
        }
        return
      }
      if (isFrameRenderedMessage(data)) {
        initialRenderCommitted = true
        dispatchInitialRender(port)
        monitor.rendered()
        return
      }
      if (isFrameQueriesSettledMessage(data)) {
        monitor.queriesSettled()
        return
      }
      if (isFrameDiagnosticsMessage(data)) {
        setDiagnostics(data.entries)
        return
      }
      if (isFrameAnnotationCopyMessage(data)) {
        // The opaque-origin frame cannot reach the clipboard; the host writes on its behalf. The
        // user's click inside our own iframe keeps this document's frame tree focused, which is
        // what the async clipboard API requires.
        navigator.clipboard.writeText(data.markdown).catch((error: unknown) => {
          console.error('Guest annotation clipboard write failed', error)
        })
        return
      }
      if (isFrameResourceSnapshotMessage(data)) {
        const pending = pendingResourceSnapshots.get(data.id)
        if (pending !== undefined) {
          window.clearTimeout(pending.timeoutId)
          pending.resolve(data)
          pendingResourceSnapshots.delete(data.id)
        }
        return
      }
      if (isFrameDestinationResolveMessage(data)) {
        if (activeRequests >= GUEST_QUERY_BUDGETS.maxConcurrentQueryRequests) {
          queuedDispatches.push(() => startDestinationResolve(port, data))
        } else {
          startDestinationResolve(port, data)
        }
        return
      }
      if (isFrameDestinationRequestMessage(data)) {
        onGuestDestinationRequestRef.current?.({
          destination: data.destination,
          newTab: data.newTab === true,
        })
        return
      }
      if (isFrameNavigationRequestMessage(data)) {
        // Guest-space hrefs, branched BEFORE URL resolution so they can never masquerade as
        // same-origin host links; the mount's prefix translates them for a new tab.
        if (data.href.startsWith('/')) {
          const prefix = guestSpaceHrefPrefixRef.current
          if (prefix === undefined) {
            return
          }
          const guestRoute = parseGuestHref(data.href)
          if (guestRoute === undefined) {
            return
          }
          const guestPath = guestRoute.path === '/' ? '' : guestRoute.path
          openExternalTabRef.current(`${prefix}${guestPath}${guestRoute.search}`)
          return
        }
        let url: URL
        try {
          url = new URL(data.href)
        } catch {
          return
        }
        if (url.protocol === 'mailto:' || url.protocol === 'tel:') {
          launchProtocolHandler(data.href)
          return
        }
        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
          return
        }
        if (url.origin !== window.location.origin) {
          // External links always open a new tab; the current tab never leaves the app.
          openExternalTabRef.current(url.href)
          return
        }
        onHostNavigationRequestRef.current?.({
          pathname: url.pathname,
          search: url.search,
          newTab: data.newTab === true,
        })
        return
      }
      if (isFrameNavigateMessage(data)) {
        if (onGuestNavigateRef.current === undefined) {
          return
        }
        // Authoritative re-check: the host never trusts frame-side validation.
        const path = sanitizeGuestPath(data.path)
        const search = sanitizeGuestSearch(data.search ?? '')
        if (path === undefined || search === undefined) {
          console.error('Guest navigation dropped: invalid route', data.path)
          return
        }
        // Over-budget pushes degrade to replace, never drop (see pushRate in protocol.ts).
        const now = Date.now()
        let oldest = pushTimestamps.at(0)
        while (oldest !== undefined && now - oldest > GUEST_NAVIGATION_BUDGETS.pushRate.windowMs) {
          pushTimestamps.shift()
          oldest = pushTimestamps.at(0)
        }
        const overBudget = pushTimestamps.length >= GUEST_NAVIGATION_BUDGETS.pushRate.maxPushes
        const replace = data.replace === true || overBudget
        if (!replace) {
          pushTimestamps.push(now)
        }
        onGuestNavigateRef.current({ path, search, replace })
        return
      }
      if (isFrameHistoryGoMessage(data)) {
        onGuestHistoryGoRef.current?.(data.delta)
        return
      }
      if (isFrameTitleMessage(data)) {
        onGuestTitleRef.current?.(data.title)
        return
      }
      if (isFrameSqlRequestMessage(data)) {
        if (
          data.sql.length === 0 ||
          data.sql.length > GUEST_QUERY_BUDGETS.maxSerializedQueryLength
        ) {
          rejectRequest(
            port,
            data.id,
            `SQL text must be between 1 and ${GUEST_QUERY_BUDGETS.maxSerializedQueryLength} characters`,
          )
          recordClientQueryFailure('query_validation')
          return
        }
        if (!initialRenderCommitted) {
          initialSqlRequests.push(data)
          return
        }
        dispatchRequest(port, data.id, statementKeyOf(data), (traceparent) => {
          return runSql(data.sql, {
            page: data.page,
            ...(data.includeTotalCount === true ? { includeTotalCount: true } : {}),
            ...(data.refresh === true ? { refresh: true } : {}),
            traceparent,
          })
        })
        return
      }
      if (isFrameEndpointCallMessage(data)) {
        const inputBytes = jsonByteLength(data.input)
        if (inputBytes === null || inputBytes > GUEST_ENDPOINT_BUDGETS.maxInputBytes) {
          port.postMessage(
            endpointErrorOf(
              data.id,
              new EndpointCallError(
                `Endpoint input must be JSON of at most ${GUEST_ENDPOINT_BUDGETS.maxInputBytes} bytes`,
                'payload_too_large',
                413,
              ),
            ),
          )
          return
        }
        if (activeRequests >= GUEST_QUERY_BUDGETS.maxConcurrentQueryRequests) {
          queuedDispatches.push(() => startEndpointCall(port, data))
        } else {
          startEndpointCall(port, data)
        }
        return
      }
      if (isFrameGraphqlRequestMessage(data)) {
        const variablesBytes = jsonByteLength(data.variables)
        if (variablesBytes === null || variablesBytes > GUEST_ENDPOINT_BUDGETS.maxInputBytes) {
          port.postMessage({
            type: 'guest-graphql-error',
            id: data.id,
            error: `GraphQL variables must be JSON of at most ${GUEST_ENDPOINT_BUDGETS.maxInputBytes} bytes`,
          })
          return
        }
        if (activeRequests >= GUEST_QUERY_BUDGETS.maxConcurrentQueryRequests) {
          queuedDispatches.push(() => startGraphql(port, data))
        } else {
          startGraphql(port, data)
        }
        return
      }
      // Reject routable malformed requests so the frame does not wait forever.
      const candidate = data as { type?: unknown; id?: unknown } | null
      if (typeof data === 'object' && candidate !== null && candidate.type === 'guest-sql') {
        if (isRequestId(candidate.id)) {
          rejectRequest(port, candidate.id, 'Invalid guest SQL request')
          recordClientQueryFailure('query_validation')
        }
      }
      if (
        typeof data === 'object' &&
        candidate?.type === 'guest-endpoint-call' &&
        isRequestId(candidate.id)
      ) {
        port.postMessage(endpointErrorOf(candidate.id, new Error('Invalid endpoint call')))
      }
      if (
        typeof data === 'object' &&
        candidate?.type === 'guest-graphql' &&
        isRequestId(candidate.id)
      ) {
        port.postMessage({
          type: 'guest-graphql-error',
          id: candidate.id,
          error: 'Invalid GraphQL request',
        })
      }
    }

    // LEGACY_CJS_REMOVE: ENG-601 — Retire only the legacy ready/init branches after ESM-only cutover.
    // Keep the host authorization, SQL dispatch, routing, diagnostics and teardown behavior.
    const handleBootstrapMessage = (event: MessageEvent<unknown>): void => {
      const frameWindow = iframeRef.current?.contentWindow
      // only to bootstrap a one-document MessagePort capability. Requests are
      // never authorized by a WindowProxy (which survives document navigation).
      if (initialized || frameWindow == null || event.source !== frameWindow) {
        return
      }
      // A legacy frame reports the build it was cached as; an App build is identified by its hash.
      let ready: { buildSha: string; skew: boolean }
      if (boot.kind === 'esm') {
        if (!isAppFrameReadyMessage(event.data)) {
          return
        }
        ready = {
          buildSha: boot.sha256,
          skew: event.data.protocolVersion !== APP_SDK_PROTOCOL_VERSION,
        }
      } else {
        if (!isFrameReadyMessage(event.data)) {
          return
        }
        ready = {
          buildSha: event.data.buildSha,
          skew: event.data.protocolVersion !== GUEST_FRAME_PROTOCOL_VERSION,
        }
      }
      runtimeBuildSha = ready.buildSha
      // Version skew (a stale cached frame vs a newer host): refuse rather than half-speak.
      if (ready.skew) {
        window.clearTimeout(bootTimer)
        setFrameError('Guest runtime is out of date — reload to update.')
        monitor.failed('protocol_mismatch')
        captureFailureOnce('protocol_mismatch')
        return
      }

      initialized = true
      window.clearTimeout(bootTimer)
      monitor.ready(ready.buildSha)
      const channel = new MessageChannel()
      rpcPortRef.current = channel.port1
      channel.port1.addEventListener('message', (rpcEvent) =>
        handleRpcMessage(channel.port1, rpcEvent),
      )
      channel.port1.start()
      monitor.setResourceRequester((includeMemory) =>
        requestResourceSnapshot(channel.port1, includeMemory),
      )
      // The mount's URL state at handshake time; later changes ride the live port.
      const route =
        routeRef.current === undefined
          ? {}
          : { route: { path: routeRef.current.path, search: routeRef.current.search } }
      frameWindow.postMessage(
        boot.kind === 'esm'
          ? {
              type: 'app-init',
              protocolVersion: APP_SDK_PROTOCOL_VERSION,
              annotationsEnabled: isInternal,
              // The legacy frame bakes the AG Grid license into its own document; the ESM shell is
              // shared CDN bytes, so the host hands the key over at init instead.
              ...(typeof __AG_GRID_LICENSE_KEY__ === 'string' && __AG_GRID_LICENSE_KEY__ !== ''
                ? { licenseKey: __AG_GRID_LICENSE_KEY__ }
                : {}),
              ...route,
              ...(boot.mappings === undefined ? {} : { mappings: boot.mappings }),
            }
          : {
              type: 'guest-init',
              protocolVersion: GUEST_FRAME_PROTOCOL_VERSION,
              code: boot.code,
              annotationsEnabled: isInternal,
              ...route,
            },
        '*',
        [channel.port2],
      )
    }

    window.addEventListener('message', handleBootstrapMessage)
    return () => {
      disposed = true
      queuedDispatches.length = 0
      window.clearTimeout(bootTimer)
      window.removeEventListener('message', handleBootstrapMessage)
      rpcPortRef.current?.close()
      rpcPortRef.current = null
      for (const pending of pendingResourceSnapshots.values()) {
        window.clearTimeout(pending.timeoutId)
        pending.reject(new Error('Guest frame unmounted before resource snapshot completed'))
      }
      pendingResourceSnapshots.clear()
      monitor.unregister()
    }
    // telemetryContext must be referentially stable (callers memoize it): a new identity
    // tears down and re-boots the whole frame. isInternal is settled by the time embed views
    // render; a mid-session flip (rare) re-boots the frame, which is correct if wasteful.
  }, [boot, frameEpoch, isInternal, runSql, runSqlBatch, source, telemetryContext, t])

  // Every host URL change rides the live port (a reboot would destroy guest state); a port
  // that isn't up yet drops the post — guest-init carries the route at handshake time.
  useEffect(() => {
    if (route === undefined) {
      return
    }
    rpcPortRef.current?.postMessage({
      type: 'guest-route-change',
      path: route.path,
      search: route.search,
      key: route.key,
    })
    // Spurious re-posts commit as no-ops frame-side.
  }, [route])

  return (
    <section aria-label={title} className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="relative flex min-h-0 grow flex-col">
        {boot.kind === 'error' || frameError !== undefined ? (
          <GuestFrameFailureNotice />
        ) : (
          <iframe
            key={`${String(frameEpoch)}:${boot.kind === 'esm' ? boot.sha256 : source}`}
            ref={iframeRef}
            // pointer-lock is opt-in: the sandboxed opaque origin never matches the default 'self'.
            allow="camera 'none'; display-capture 'none'; geolocation 'none'; microphone 'none'; payment 'none'; pointer-lock; usb 'none'"
            className="size-full min-h-0 grow border-0"
            onLoad={handleFrameLoad}
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-downloads allow-pointer-lock"
            title={title}
            {...(boot.kind === 'esm' ? { srcDoc: boot.srcdoc } : { src: GUEST_FRAME_PATH })}
          />
        )}
        <GuestFrameDebugOverlay
          className="absolute end-2 top-2 z-20"
          compileError={boot.kind === 'error' ? boot.message : undefined}
          entries={diagnostics}
          frameError={frameError}
          lintFindings={lintFindings}
          runtimeErrors={runtimeErrors}
          showInternalDetail={isInternal}
        />
        {showBetaWatermark ? <GuestFrameBetaWatermark /> : null}
      </div>
    </section>
  )
})

GuestFrame.displayName = 'GuestFrame'

/**
 * Body state for a guest that cannot run: compile error, boot timeout, protocol mismatch, or a
 * runtime throw. States only that it failed; the message itself lives in the debug overlay.
 */
function GuestFrameFailureNotice(): JSX.Element {
  const { t } = useTypedTranslation()
  return (
    <div className="flex size-full items-center justify-center p-4">
      <div className="flex max-w-xs flex-col items-center gap-1.5 rounded-md border border-gray-4 bg-gray-1 px-5 py-4 text-center">
        <Bug className="text-gray-9" size={16} />
        <p className="text-sm font-medium leading-5 text-gray-12">{t('embed.frameFailed')}</p>
        <p className="text-xs leading-4 text-gray-9">{t('embed.frameFailedHint')}</p>
      </div>
    </div>
  )
}

/**
 * Tiled diagonal "BETA" watermark over the frame body while BYOFE is in beta.
 * Rendered in the HOST document, not the guest iframe, so guest code can
 * never reach or remove it. Inert by contract: pointer-events/selection
 * disabled and aria-hidden, so it never intercepts interaction or reaches
 * screen readers. The pattern repeats at fixed density, so any visible
 * region of any embed carries the mark; textLength pins the glyph run so
 * tile geometry does not depend on the host font's metrics. Delete this
 * component (and its single callsite above) when BYOFE exits beta.
 */
function GuestFrameBetaWatermark(): JSX.Element {
  // Pattern ids are document-global; useId keeps multiple frames on one
  // page from colliding (colons stripped: they misparse inside url(#...)).
  const patternId = `guest-frame-beta-${useId().replace(/:/g, '')}`
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-10 select-none overflow-hidden"
    >
      <svg className="size-full text-gray-12 opacity-[0.03]">
        <defs>
          <pattern
            height="110"
            id={patternId}
            patternTransform="rotate(-24)"
            patternUnits="userSpaceOnUse"
            width="170"
          >
            <text
              dominantBaseline="central"
              fill="currentColor"
              fontSize="34"
              fontWeight="900"
              lengthAdjust="spacingAndGlyphs"
              letterSpacing="0.08em"
              textLength="110"
              x="10"
              y="55"
            >
              BETA
            </text>
          </pattern>
        </defs>
        <rect fill={`url(#${patternId})`} height="100%" width="100%" />
      </svg>
    </div>
  )
}
